import { Box, HStack, Stack } from "@chakra-ui/react";
import React from "react";
import { AddressModel } from "../../../api/models";
import {
  addressMaxLength,
  cityMaxLength,
  stateMaxLength,
  zipCodeMaxLength,
} from "../../../defines";
import usaStates from "../../../defines/usaStates";
import { Controller, FieldValues, Path } from "../../../react-hook-form";
import { VeriCountrySelect } from "../drop-down";
import { FormControlWrapper } from "../FormControlWrapper";
import { VeriInput } from "../VeriInput";
import { VeriSelect } from "../VeriSelect";
import { AutocompleteAddress } from "./AutocompleteAddress";
import { HookFormVeriAddressProps } from "./HookFormVeriAddress.types";

export const HookFormVeriAddress = <T extends FieldValues>({
  form,
  name,
  rules,
  alternativeLabels,
  onSelect,
  disallowPOBox,
  isDisabled,
  spacing = 6,
  dataTestId,
  secondAddressProps,
  usaAsDefaultCountry = true,
  ...rest
}: HookFormVeriAddressProps<T>) => {
  const address: AddressModel | undefined =
    (form.watch(name) as AddressModel) || undefined;
  const country = address?.country || "";

  React.useEffect(() => {
    if (usaAsDefaultCountry && !country) {
      form.setValue(`${name}.country` as never, "US" as never);
    }
  }, [usaAsDefaultCountry, country]);

  const onAddressChange = (
    onChange: (value: string) => void,
    value: string,
  ) => {
    onChange(value);
    form.setValue(`${name}.id` as never, null as never);
  };

  return (
    <Controller
      rules={{
        required: rest.isRequired ? "You must provide an address" : undefined,
        ...rules,
      }}
      control={form.control}
      name={name}
      render={() => (
        <FormControlWrapper
          data-testid={dataTestId}
          isDisabled={isDisabled}
          {...rest}
        >
          <Stack w={"100%"} spacing={spacing}>
            <Box>
              <Controller
                render={({ field }) => (
                  <input type="hidden" value={(field.value || "") as string} />
                )}
                control={form.control}
                name={`${name}.id` as Path<T>}
              />
              <Controller
                rules={{
                  required: rest.isRequired
                    ? "You must provide an address"
                    : undefined,
                }}
                render={({ field, fieldState }) => (
                  <AutocompleteAddress
                    ref={field.ref}
                    form={form}
                    inputName={field.name}
                    baseName={name}
                    label={alternativeLabels?.address1 || "Address 1"}
                    error={fieldState.error?.message}
                    onSelect={onSelect}
                    onChange={(value) =>
                      onAddressChange(field.onChange, value || "")
                    }
                    idDefaultValue={null}
                    inputProps={{
                      isRequired: rest.isRequired,
                      optional: !rest.isRequired,
                      helperText: disallowPOBox
                        ? "This address can not be a P.O. Box"
                        : undefined,
                      isDisabled,
                    }}
                  />
                )}
                control={form.control}
                name={`${name}.address1` as Path<T>}
              />
            </Box>
            <Controller
              render={({ field, fieldState }) => (
                <VeriInput
                  ref={field.ref}
                  optional
                  label={alternativeLabels?.address2 || "Address 2"}
                  error={fieldState.error?.message}
                  onChange={(e) =>
                    onAddressChange(field.onChange, e.currentTarget.value || "")
                  }
                  value={(field.value as string) || ""}
                  helperText={
                    disallowPOBox && "This address can not be a P.O. Box"
                  }
                  maxLength={addressMaxLength}
                  isDisabled={isDisabled}
                  {...secondAddressProps}
                />
              )}
              control={form.control}
              name={`${name}.address2` as Path<T>}
            />
            <HStack spacing={6}>
              <Controller
                rules={{
                  required: rest.isRequired
                    ? "You must provide a city"
                    : undefined,
                }}
                render={({ field, fieldState }) => (
                  <VeriInput
                    optional={!rest.isRequired}
                    ref={field.ref}
                    onChange={(e) =>
                      onAddressChange(
                        field.onChange,
                        e.currentTarget.value || "",
                      )
                    }
                    value={(field.value as string) || ""}
                    label="City"
                    error={fieldState.error?.message}
                    maxLength={cityMaxLength}
                    isRequired={rest.isRequired}
                    isDisabled={isDisabled}
                  />
                )}
                control={form.control}
                name={`${name}.city` as Path<T>}
              />
              <Controller
                rules={{
                  required: rest.isRequired
                    ? "You must provide a region"
                    : undefined,
                }}
                render={({ field, fieldState }) =>
                  country === "US" ? (
                    <VeriSelect
                      ref={field.ref}
                      onChange={(e) =>
                        onAddressChange(
                          field.onChange,
                          e.currentTarget.value || "",
                        )
                      }
                      value={(field.value as string) || ""}
                      label="State"
                      error={fieldState.error?.message}
                      isRequired={rest.isRequired}
                      isOptional={!rest.isRequired}
                      isDisabled={isDisabled}
                    >
                      <option value="">Select</option>
                      {usaStates.map((state) => (
                        <option
                          key={state.abbreviation}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      ))}
                    </VeriSelect>
                  ) : (
                    <VeriInput
                      ref={field.ref}
                      onChange={(e) =>
                        onAddressChange(
                          field.onChange,
                          e.currentTarget.value || "",
                        )
                      }
                      optional={!rest.isRequired}
                      value={(field.value as string) || ""}
                      label="State"
                      error={fieldState.error?.message}
                      maxLength={stateMaxLength}
                      isDisabled={isDisabled}
                    />
                  )
                }
                control={form.control}
                name={`${name}.region` as Path<T>}
              />
            </HStack>
            <HStack spacing={6}>
              <Controller
                rules={{
                  required: rest.isRequired
                    ? "You must provide a zip code"
                    : undefined,
                }}
                render={({ field, fieldState }) => (
                  <VeriInput
                    ref={field.ref}
                    onChange={(e) =>
                      onAddressChange(
                        field.onChange,
                        e.currentTarget.value || "",
                      )
                    }
                    optional={!rest.isRequired}
                    value={(field.value as string) || ""}
                    label="Zip code"
                    error={fieldState.error?.message}
                    maxLength={zipCodeMaxLength}
                    isRequired={rest.isRequired}
                    isDisabled={isDisabled}
                  />
                )}
                control={form.control}
                name={`${name}.postalCode` as Path<T>}
              />
              <Controller
                rules={{
                  required: rest.isRequired
                    ? "You must provide a country"
                    : undefined,
                }}
                render={({ field, fieldState }) => (
                  <VeriCountrySelect
                    ref={field.ref}
                    label="Country"
                    onChange={(e) =>
                      onAddressChange(
                        field.onChange,
                        e.currentTarget.value || "",
                      )
                    }
                    value={(field.value as string) || ""}
                    error={fieldState.error?.message}
                    isRequired={rest.isRequired}
                    isOptional={!rest.isRequired}
                    isDisabled={isDisabled}
                    addEmptyOptionCountry={!usaAsDefaultCountry}
                  />
                )}
                control={form.control}
                name={`${name}.country` as Path<T>}
              />
            </HStack>
          </Stack>
        </FormControlWrapper>
      )}
    />
  );
};
